(function () {
    // Keep words whole by default and opt in only words wider than their line.
    const targetSelector = [
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'p',
        'li',
        'dt',
        'dd',
        'figcaption',
        'blockquote',
        'button',
        'label',
        'legend',
        'summary',
        'td',
        'th',
        '.accessibility-settings__title',
        '.accordion__header-title',
        '.directions__group-title',
        '.useful-links__text',
        '.about-message__title',
        '.accordion__content-teacher-name',
        '.title-block'
    ].join(',');
    const wordClass = 'selective-hyphenation__word';
    const wordSelector = `.${wordClass}`;
    const wordPattern = /[\p{L}\p{M}]+/gu;
    const trailingPunctuationPattern = /^[,.;:!?…%‰»”’\)\]\}]+/u;
    const measurementTolerance = 0.05;

    document.addEventListener('DOMContentLoaded', function () {
        const measurementProbe = document.createElement('kf21-text-measure');
        const measurementCache = new WeakMap();
        const observedTargetWidths = new WeakMap();
        const observedTargets = new Set();
        let resizeObserver = null;
        let mutationObserver = null;
        let scheduledFrame = null;

        measurementProbe.setAttribute('aria-hidden', 'true');
        measurementProbe.style.all = 'unset';
        measurementProbe.style.position = 'absolute';
        measurementProbe.style.left = '-10000px';
        measurementProbe.style.top = '0';
        measurementProbe.style.display = 'inline-block';
        measurementProbe.style.visibility = 'hidden';
        measurementProbe.style.pointerEvents = 'none';
        measurementProbe.style.margin = '0';
        measurementProbe.style.padding = '0';
        measurementProbe.style.border = '0';
        measurementProbe.style.whiteSpace = 'nowrap';
        measurementProbe.style.hyphens = 'none';
        document.body.appendChild(measurementProbe);

        const getHorizontalScale = function (element, rect) {
            return element.offsetWidth > 0
                ? rect.width / element.offsetWidth
                : 1;
        };

        const getContentRect = function (element) {
            const styles = window.getComputedStyle(element);
            const rect = element.getBoundingClientRect();
            const horizontalScale = getHorizontalScale(element, rect);
            const leftInset = (
                (Number.parseFloat(styles.borderLeftWidth) || 0)
                + (Number.parseFloat(styles.paddingLeft) || 0)
            ) * horizontalScale;
            const rightInset = (
                (Number.parseFloat(styles.borderRightWidth) || 0)
                + (Number.parseFloat(styles.paddingRight) || 0)
            ) * horizontalScale;
            const left = rect.left + leftInset;
            const right = rect.right - rightInset;

            return {
                left,
                right,
                width: Math.max(0, right - left)
            };
        };

        const getInnerRect = function (element) {
            const styles = window.getComputedStyle(element);
            const rect = element.getBoundingClientRect();
            const horizontalScale = getHorizontalScale(element, rect);
            const left = rect.left
                + (Number.parseFloat(styles.borderLeftWidth) || 0) * horizontalScale;
            const right = rect.right
                - (Number.parseFloat(styles.borderRightWidth) || 0) * horizontalScale;

            return { left, right };
        };

        const getContentWidth = function (element) {
            return getContentRect(element).width;
        };

        const getAvailableWidth = function (textParent, target) {
            let availableWidth = getContentWidth(target);
            let current = textParent;
            let reachedTarget = false;
            const targetContentRect = getContentRect(target);

            while (current) {
                const styles = window.getComputedStyle(current);
                const display = styles.display;

                if (current === target) {
                    reachedTarget = true;
                }

                if (display !== 'inline' && display !== 'contents') {
                    let currentWidth = getContentWidth(current);

                    if (reachedTarget && current !== target) {
                        const innerRect = getInnerRect(current);

                        currentWidth = Math.max(
                            0,
                            Math.min(targetContentRect.right, innerRect.right)
                            - Math.max(targetContentRect.left, innerRect.left)
                        );
                    }

                    if (currentWidth > 0) {
                        availableWidth = Math.min(availableWidth, currentWidth);
                    }
                }

                if (
                    styles.position === 'absolute'
                    || styles.position === 'fixed'
                    || current === document.body
                ) {
                    break;
                }

                current = current.parentElement;
            }

            return availableWidth;
        };

        const getVisualContextSignature = function (sourceElement) {
            const signature = [];
            let current = sourceElement;

            while (current && current !== document.documentElement) {
                const styles = window.getComputedStyle(current);

                if (styles.transform !== 'none' || styles.zoom !== '1') {
                    signature.push(
                        styles.transform,
                        styles.transformOrigin,
                        styles.zoom
                    );
                }

                current = current.parentElement;
            }

            return signature.join('|');
        };

        const createWordMeasurer = function (sourceElement) {
            const styles = window.getComputedStyle(sourceElement);
            const signature = [
                styles.fontFamily,
                styles.fontSize,
                styles.fontStyle,
                styles.fontWeight,
                styles.fontStretch,
                styles.fontVariant,
                styles.fontKerning,
                styles.fontFeatureSettings,
                styles.fontVariationSettings,
                styles.letterSpacing,
                styles.textTransform,
                getVisualContextSignature(sourceElement)
            ].join('|');
            let cache = measurementCache.get(sourceElement);
            let isProbeAttached = false;

            if (!cache || cache.signature !== signature) {
                cache = {
                    signature,
                    widths: new Map()
                };
                measurementCache.set(sourceElement, cache);
            }

            measurementProbe.style.fontFamily = styles.fontFamily;
            measurementProbe.style.fontSize = styles.fontSize;
            measurementProbe.style.fontStyle = styles.fontStyle;
            measurementProbe.style.fontWeight = styles.fontWeight;
            measurementProbe.style.fontStretch = styles.fontStretch;
            measurementProbe.style.fontVariant = styles.fontVariant;
            measurementProbe.style.fontKerning = styles.fontKerning;
            measurementProbe.style.fontFeatureSettings = styles.fontFeatureSettings;
            measurementProbe.style.fontVariationSettings = styles.fontVariationSettings;
            measurementProbe.style.letterSpacing = styles.letterSpacing;
            measurementProbe.style.textTransform = styles.textTransform;

            const measure = function (word) {
                if (cache.widths.has(word)) {
                    return cache.widths.get(word);
                }

                if (!isProbeAttached) {
                    measurementProbe.style.left = '0';
                    sourceElement.appendChild(measurementProbe);
                    isProbeAttached = true;
                }

                measurementProbe.textContent = word;

                const wordWidth = measurementProbe.getBoundingClientRect().width;

                cache.widths.set(word, wordWidth);

                return wordWidth;
            };

            measure.release = function () {
                measurementProbe.textContent = '';

                if (!isProbeAttached) {
                    return;
                }

                document.body.appendChild(measurementProbe);
                measurementProbe.style.left = '-10000px';
                isProbeAttached = false;
            };

            return measure;
        };

        const unwrapMeasuredWords = function () {
            document.querySelectorAll(wordSelector).forEach((word) => {
                word.replaceWith(document.createTextNode(word.textContent));
            });
        };

        const isVisuallyHidden = function (element, target) {
            let current = element;

            while (current && current !== target.parentElement) {
                const styles = window.getComputedStyle(current);
                const rect = current.getBoundingClientRect();
                const hasClipping = styles.clip !== 'auto' || styles.clipPath !== 'none';
                const isOnePixelClip = styles.position === 'absolute'
                    && styles.overflow === 'hidden'
                    && rect.width <= 1.5
                    && rect.height <= 1.5;

                if (
                    styles.display === 'none'
                    || styles.visibility === 'hidden'
                    || hasClipping
                    || isOnePixelClip
                ) {
                    return true;
                }

                current = current.parentElement;
            }

            return false;
        };

        const collectTextNodes = function (target) {
            const walker = document.createTreeWalker(
                target,
                NodeFilter.SHOW_TEXT,
                {
                    acceptNode: function (node) {
                        if (!node.nodeValue || !wordPattern.test(node.nodeValue)) {
                            wordPattern.lastIndex = 0;
                            return NodeFilter.FILTER_REJECT;
                        }

                        wordPattern.lastIndex = 0;

                        if (
                            node.parentElement.closest(wordSelector)
                            || isVisuallyHidden(node.parentElement, target)
                        ) {
                            return NodeFilter.FILTER_REJECT;
                        }

                        return NodeFilter.FILTER_ACCEPT;
                    }
                }
            );
            const nodes = [];
            let current = walker.nextNode();

            while (current) {
                nodes.push(current);
                current = walker.nextNode();
            }

            return nodes;
        };

        const wrapOverlongWords = function (target) {
            if (!target.isConnected || target.getClientRects().length === 0) {
                return;
            }

            collectTextNodes(target).forEach((textNode) => {
                const textParent = textNode.parentElement;
                const whiteSpace = window.getComputedStyle(textParent).whiteSpace;

                if (whiteSpace === 'nowrap' || whiteSpace === 'pre') {
                    return;
                }

                const availableWidth = getAvailableWidth(textParent, target);

                if (availableWidth <= 0) {
                    return;
                }

                const text = textNode.nodeValue;
                const fragment = document.createDocumentFragment();
                const measureWord = createWordMeasurer(textParent);
                let lastIndex = 0;
                let hasOverlongWord = false;

                wordPattern.lastIndex = 0;

                for (const match of text.matchAll(wordPattern)) {
                    const word = match[0];
                    const startIndex = match.index;
                    const wordEndIndex = startIndex + word.length;
                    const trailingPunctuation = text
                        .slice(wordEndIndex)
                        .match(trailingPunctuationPattern)?.[0] || '';
                    const measuredToken = word + trailingPunctuation;

                    fragment.append(document.createTextNode(text.slice(lastIndex, startIndex)));

                    if (
                        measureWord(measuredToken)
                        > availableWidth + measurementTolerance
                    ) {
                        const wordElement = document.createElement('span');

                        wordElement.className = wordClass;
                        wordElement.textContent = measuredToken;
                        fragment.append(wordElement);
                        hasOverlongWord = true;
                        lastIndex = wordEndIndex + trailingPunctuation.length;
                    } else {
                        fragment.append(document.createTextNode(word));
                        lastIndex = wordEndIndex;
                    }
                }

                measureWord.release();
                wordPattern.lastIndex = 0;

                if (!hasOverlongWord) {
                    return;
                }

                fragment.append(document.createTextNode(text.slice(lastIndex)));
                textNode.replaceWith(fragment);
            });
        };

        const observeTargets = function (targets) {
            if (!resizeObserver) {
                return;
            }

            observedTargets.forEach((target) => {
                if (!targets.includes(target)) {
                    resizeObserver.unobserve(target);
                    observedTargets.delete(target);
                    observedTargetWidths.delete(target);
                }
            });

            targets.forEach((target) => {
                observedTargetWidths.set(target, getContentWidth(target));

                if (!observedTargets.has(target)) {
                    resizeObserver.observe(target);
                    observedTargets.add(target);
                }
            });
        };

        const applySelectiveHyphenation = function () {
            scheduledFrame = null;

            if (mutationObserver) {
                mutationObserver.disconnect();
            }

            unwrapMeasuredWords();

            const targets = [...document.querySelectorAll(targetSelector)];

            targets.forEach(wrapOverlongWords);
            observeTargets(targets);

            if (mutationObserver) {
                mutationObserver.observe(document.body, {
                    attributes: true,
                    attributeFilter: [
                        'data-accessibility',
                        'data-font-size',
                        'data-no-images'
                    ],
                    childList: true,
                    characterData: true,
                    subtree: true
                });
            }
        };

        const scheduleSelectiveHyphenation = function () {
            if (scheduledFrame !== null) {
                return;
            }

            scheduledFrame = window.requestAnimationFrame(() => {
                scheduledFrame = window.requestAnimationFrame(applySelectiveHyphenation);
            });
        };

        if ('ResizeObserver' in window) {
            resizeObserver = new ResizeObserver((entries) => {
                const hasWidthChange = entries.some((entry) => {
                    const currentWidth = getContentWidth(entry.target);
                    const previousWidth = observedTargetWidths.get(entry.target);

                    observedTargetWidths.set(entry.target, currentWidth);

                    return previousWidth !== undefined
                        && Math.abs(currentWidth - previousWidth) > measurementTolerance;
                });

                if (hasWidthChange) {
                    scheduleSelectiveHyphenation();
                }
            });
        }
        mutationObserver = new MutationObserver((mutations) => {
            const hasRelevantMutation = mutations.some((mutation) => {
                if (mutation.type === 'attributes') {
                    return mutation.target === document.body;
                }

                const mutationElement = mutation.target.nodeType === Node.ELEMENT_NODE
                    ? mutation.target
                    : mutation.target.parentElement;

                return Boolean(
                    mutationElement
                    && (
                        mutationElement.matches(targetSelector)
                        || mutationElement.closest(targetSelector)
                        || [...mutation.addedNodes].some((node) => (
                            node.nodeType === Node.ELEMENT_NODE
                            && (node.matches(targetSelector) || node.querySelector(targetSelector))
                        ))
                    )
                );
            });

            if (hasRelevantMutation) {
                scheduleSelectiveHyphenation();
            }
        });

        const fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();

        fontsReady.catch(() => undefined).then(scheduleSelectiveHyphenation);
        window.addEventListener('resize', scheduleSelectiveHyphenation, { passive: true });
    });
}());
